// ============================================================
// Pay Rocky — Login & Signup Pages
// ============================================================

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';
import { Button, FormField, TextInput, Card } from '../components/ui/Primitives';
import { API_BASE_URL, SUPPORT_EMAIL } from '../data/constants';
import { useSupportEmail } from '../hooks/useSupportEmail';
import { getPasswordValidationMessage, isValidEmail } from '../utils/errors';
import { readStorageJSON, removeStorageItem, writeStorageJSON } from '../utils/storage';

const passwordFieldWrapperStyle = { position: 'relative' };
const passwordInputStyle = { paddingRight: '52px' };
const BACKEND_HEALTH_URL = `${API_BASE_URL.replace(/\/api\/?$/, '')}/health`;
const LOGIN_FORM_DRAFT_KEY = 'payrocky_login_form_draft';

function useAuthLoadingLabel(loading, initialLabel, delayedLabel) {
  const [label, setLabel] = useState(initialLabel);

  useEffect(() => {
    if (!loading) {
      setLabel(initialLabel);
      return undefined;
    }

    setLabel(initialLabel);
    const timeoutId = window.setTimeout(() => {
      setLabel(delayedLabel);
    }, 4500);

    return () => window.clearTimeout(timeoutId);
  }, [delayedLabel, initialLabel, loading]);

  return label;
}

// ── Shared auth layout ───────────────────────────────────────
function AuthLayout({ children, title, sub, link, linkText, linkTo }) {
  const location = useLocation();
  const returnLocation = location.state?.from || null;

  useEffect(() => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 12000) : null;

    fetch(BACKEND_HEALTH_URL, {
      method: 'GET',
      cache: 'no-store',
      signal: controller?.signal,
    }).catch(() => {});

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (controller) controller.abort();
    };
  }, []);

  return (
    <div className="auth-shell" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
      background: 'var(--clr-bg)',
    }}>
      {/* Logo */}
      <Link to="/" className="auth-logo" style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '30px', height: '30px', background: 'linear-gradient(135deg, var(--clr-accent) 0%, var(--clr-accent-2) 100%)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '14px', color: 'var(--clr-on-accent)' }}>P</div>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '17px' }}>Pay<span style={{ color: 'var(--clr-accent)' }}>Rocky</span></span>
      </Link>

      <Card className="auth-card" style={{ width: '100%', maxWidth: '420px' }}>
        <div className="auth-header" style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 800, marginBottom: '5px' }}>{title}</h1>
          <p className="auth-subtitle" style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>{sub}</p>
        </div>
        {children}
        <div className="auth-footer-copy" style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '13px', color: 'var(--clr-text-3)' }}>
          {linkText}{' '}
          <Link to={linkTo} state={returnLocation ? { from: returnLocation } : undefined} style={{ color: 'var(--clr-accent)', fontWeight: 500 }}>{link}</Link>
        </div>
      </Card>

    </div>
  );
}

// ── Login Page ────────────────────────────────────────────────
export function LoginPage() {
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnLocation = location.state?.from;
  const locationMessage = location.state?.message || '';
  const returnPath = typeof returnLocation?.pathname === 'string'
    ? `${returnLocation.pathname}${returnLocation.search || ''}${returnLocation.hash || ''}`
    : '';

  const [form, setForm]       = useState(() => {
    const stored = readStorageJSON(LOGIN_FORM_DRAFT_KEY, { email: '' });
    return { email: stored?.email || '', password: '' };
  });
  const [showPw, setShowPw]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [notice, setNotice]   = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const loadingLabel = useAuthLoadingLabel(loading, 'Signing you in...', 'Still signing you in...');

  useEffect(() => {
    writeStorageJSON(LOGIN_FORM_DRAFT_KEY, { email: form.email });
  }, [form.email]);

  useEffect(() => {
    if (locationMessage) {
      setNotice(locationMessage);
    }
  }, [locationMessage]);

  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setError(''); setNotice(''); setErrorCode(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotice('');
    if (!form.email || !form.password) { setError('Enter both your email and password.'); return; }
    if (!isValidEmail(form.email)) { setError('Enter a valid email address.'); return; }
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.success) {
      removeStorageItem(LOGIN_FORM_DRAFT_KEY);
      toast.success('Welcome back!');
      const fallbackPath = res.user?.role === 'admin' ? '/admin' : '/dashboard';
      const nextPath = typeof returnPath === 'string' && returnPath.trim() ? returnPath : fallbackPath;
      navigate(nextPath, { replace: true });
    } else {
      setErrorCode(res.code || '');
      // Enhanced error messaging for production issues
      let displayError = res.error || 'Login failed';
      if (res.error?.includes('Network') || res.error?.includes('fetch')) {
        displayError = 'Connection error. Check your internet and try again.';
      } else if (res.error?.includes('timeout') || res.error?.includes('Timeout')) {
        displayError = 'Server is taking too long to respond. Please try again.';
      }
      setError(displayError);
      console.error('[Login Error]', res.error); // For debugging
    }
  };

  const handleResendVerification = async () => {
    if (!isValidEmail(form.email)) {
      setError('Enter the email address you used to sign up first.');
      return;
    }

    setResendingVerification(true);
    const res = await resendVerification(form.email);
    setResendingVerification(false);

    if (res.success) {
      toast.success(res.message || 'Verification link sent.');
      setError('');
    } else {
      setError(res.error || 'We could not resend the verification link right now.');
    }
  };

  return (
    <AuthLayout title="Sign in" sub="Welcome back to PayRocky" link="Create account" linkText="Don't have an account?" linkTo="/signup">
      <form className="auth-form" onSubmit={handleSubmit}>
        <FormField label="Email address">
          <TextInput type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} />
        </FormField>

        <FormField label="Password">
          <div className="password-field-shell" style={passwordFieldWrapperStyle}>
            <TextInput
              className="password-input-with-toggle"
              type={showPw ? 'text' : 'password'}
              placeholder="Your password"
              value={form.password}
              onChange={set('password')}
              style={passwordInputStyle}
            />
            <button type="button" className="auth-password-toggle" onClick={() => setShowPw(!showPw)} aria-label={showPw ? 'Hide password' : 'Show password'}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FormField>

        {notice ? <div className="auth-status-message" style={{ fontSize: '12px', color: 'var(--clr-accent)', marginBottom: '1rem', textAlign: 'center', lineHeight: 1.7 }}>{notice}</div> : null}
        {error && <div className="auth-status-message" style={{ fontSize: '12px', color: 'var(--clr-danger)', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}
        {errorCode === 'AUTH_EMAIL_NOT_VERIFIED' ? (
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <button type="button" onClick={handleResendVerification} disabled={resendingVerification} style={{ fontSize: '12px', color: 'var(--clr-accent)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>
              {resendingVerification ? 'Sending verification link...' : 'Resend verification link'}
            </button>
          </div>
        ) : null}

        <div className="auth-forgot-row" style={{ textAlign: 'right', marginBottom: '1rem' }}>
          <Link to="/forgot-password" state={returnLocation ? { from: returnLocation } : undefined} style={{ fontSize: '12px', color: 'var(--clr-accent)', fontWeight: 600 }}>
            Forgot password?
          </Link>
        </div>

        <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
          {loading ? loadingLabel : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}

// ── Signup Page ───────────────────────────────────────────────
export function SignupPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnLocation = location.state?.from;
  const returnPath = typeof returnLocation?.pathname === 'string'
    ? `${returnLocation.pathname}${returnLocation.search || ''}${returnLocation.hash || ''}`
    : '';

  const [form, setForm]       = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [showPw, setShowPw]   = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors]   = useState({});
  const loadingLabel = useAuthLoadingLabel(loading, 'Creating your account...', 'Still creating your account...');

  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setErrors(prev => ({ ...prev, [k]: '' })); };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())        errs.name     = 'Full name is required';
    if (!isValidEmail(form.email)) errs.email   = 'Enter a valid email address';
    if (form.phone && !/^(\+234|0)[789][01]\d{8}$/.test(form.phone.replace(/\s/g, '')))
      errs.phone  = 'Enter a valid Nigerian phone number';
    const passwordError = getPasswordValidationMessage(form.password);
    if (passwordError) errs.password = passwordError;
    if (form.password !== form.confirm) errs.confirm = 'Passwords do not match';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const res = await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
    setLoading(false);
    if (res.success) {
      toast.success(res.message || 'Verification link sent.');
      navigate('/login', {
        replace: true,
        state: {
          from: returnLocation || undefined,
          message: `A verification link has been sent to ${res.email || form.email}. Verify your email before signing in.`,
        },
      });
    } else {
      setErrors({ email: res.error });
    }
  };

  return (
    <AuthLayout title="Create account" sub="Start trading crypto with Naira" link="Sign in" linkText="Already have an account?" linkTo="/login">
      <form className="auth-form" onSubmit={handleSubmit}>
        <FormField label="Full name" error={errors.name}>
          <TextInput placeholder="Emeka Okafor" value={form.name} onChange={set('name')} error={errors.name} />
        </FormField>

        <FormField label="Email address" error={errors.email}>
          <TextInput type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} error={errors.email} />
        </FormField>

        <FormField label="Phone number" error={errors.phone} hint="Optional — Nigerian number">
          <TextInput type="tel" placeholder="+234 801 234 5678" value={form.phone} onChange={set('phone')} error={errors.phone} />
        </FormField>

        <FormField label="Password" error={errors.password}>
          <div className="password-field-shell" style={passwordFieldWrapperStyle}>
            <TextInput
              className="password-input-with-toggle"
              type={showPw ? 'text' : 'password'}
              placeholder="Use a strong password"
              value={form.password}
              onChange={set('password')}
              error={errors.password}
              style={passwordInputStyle}
            />
            <button type="button" className="auth-password-toggle" onClick={() => setShowPw(!showPw)} aria-label={showPw ? 'Hide password' : 'Show password'}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FormField>

        <FormField label="Confirm password" error={errors.confirm}>
          <div className="password-field-shell" style={passwordFieldWrapperStyle}>
            <TextInput
              className="password-input-with-toggle"
              type={showConfirmPw ? 'text' : 'password'}
              placeholder="Repeat password"
              value={form.confirm}
              onChange={set('confirm')}
              error={errors.confirm}
              style={passwordInputStyle}
            />
            <button type="button" className="auth-password-toggle" onClick={() => setShowConfirmPw(!showConfirmPw)} aria-label={showConfirmPw ? 'Hide password' : 'Show password'}>
              {showConfirmPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FormField>

        <div className="auth-helper-copy" style={{ fontSize: '12px', color: 'var(--clr-text-3)', marginBottom: '1rem', lineHeight: 1.6 }}>
          Password must include at least 8 characters, an uppercase letter, a lowercase letter, a number, and a special character.
        </div>

        <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
          {loading ? loadingLabel : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const resolvedSupportEmail = useSupportEmail(SUPPORT_EMAIL);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [supportEmail, setSupportEmail] = useState(resolvedSupportEmail);
  const loadingLabel = useAuthLoadingLabel(loading, 'Preparing reset link...', 'Still preparing your reset link...');

  useEffect(() => {
    setSupportEmail((prev) => prev || resolvedSupportEmail);
  }, [resolvedSupportEmail]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setSupportEmail(resolvedSupportEmail);

    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return;
    }

    setLoading(true);
    const res = await forgotPassword(email);
    setLoading(false);

    if (res.success) {
      setMessage(res.message || 'If an account exists with that email, a reset link has been prepared.');
      if (res.supportEmail) {
        setSupportEmail(res.supportEmail);
      }
      toast.success('Password reset request submitted.');
    } else {
      const fallbackSupportEmail = res.supportEmail || resolvedSupportEmail;
      setSupportEmail(fallbackSupportEmail);
      setError(
        res.error
        || `We could not prepare your password reset right now. Please contact PayRocky support at ${fallbackSupportEmail}.`
      );
    }
  };

  return (
    <AuthLayout title="Forgot password" sub="Enter your email to receive a password reset link." link="Back to sign in" linkText="Remembered your password?" linkTo="/login">
      <form className="auth-form" onSubmit={handleSubmit}>
        <FormField label="Email address" error={error}>
          <TextInput type="email" placeholder="you@example.com" value={email} onChange={(event) => { setEmail(event.target.value); setError(''); }} error={error} />
        </FormField>

        {message && <div className="auth-status-message" style={{ fontSize: '12px', color: 'var(--clr-accent)', marginBottom: '1rem', textAlign: 'center', lineHeight: 1.6 }}>{message}</div>}
        {error && (
          <div className="auth-status-message" style={{ fontSize: '12px', color: 'var(--clr-danger)', marginBottom: '1rem', textAlign: 'center', lineHeight: 1.7 }}>
            {error}
            {supportEmail ? (
              <div style={{ marginTop: '6px' }}>
                Support email:{' '}
                <a href={`mailto:${supportEmail}`} style={{ color: 'var(--clr-accent)', fontWeight: 700 }}>
                  {supportEmail}
                </a>
              </div>
            ) : null}
          </div>
        )}
        <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
          {loading ? loadingLabel : 'Send reset link'}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const { token = '' } = useParams();
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function runVerification() {
      if (!token) {
        if (!cancelled) {
          setError('This verification link is missing or invalid.');
          setLoading(false);
        }
        return;
      }

      const res = await verifyEmail(token);
      if (cancelled) return;

      if (res.success) {
        setMessage(res.message || 'Your email has been verified successfully.');
        toast.success(res.message || 'Email verified successfully.');
        window.setTimeout(() => {
          navigate('/login', { replace: true });
        }, 1800);
      } else {
        setError(res.error || 'We could not verify your email right now.');
      }
      setLoading(false);
    }

    runVerification();
    return () => {
      cancelled = true;
    };
  }, [navigate, token, verifyEmail]);

  return (
    <AuthLayout title="Verify email" sub="We are confirming your PayRocky account now." link="Back to sign in" linkText="Already verified?" linkTo="/login">
      <div style={{ textAlign: 'center' }}>
        {loading ? <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Verifying your email link...</div> : null}
        {message ? <div style={{ fontSize: '13px', color: 'var(--clr-accent)', lineHeight: 1.7 }}>{message}</div> : null}
        {error ? <div style={{ fontSize: '13px', color: 'var(--clr-danger)', lineHeight: 1.7 }}>{error}</div> : null}
      </div>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnLocation = location.state?.from;
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const loadingLabel = useAuthLoadingLabel(loading, 'Resetting password...', 'Still resetting your password...');

  const set = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!token) nextErrors.password = 'This password reset link is missing or invalid.';
    const passwordError = getPasswordValidationMessage(form.password);
    if (passwordError) nextErrors.password = passwordError;
    if (form.password !== form.confirm) nextErrors.confirm = 'Passwords do not match';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const res = await resetPassword({ token, password: form.password });
    setLoading(false);

    if (res.success) {
      toast.success(res.message || 'Password reset successful.');
      navigate('/login', { replace: true, state: returnLocation ? { from: returnLocation } : undefined });
    } else {
      setErrors({ password: res.error || 'We could not reset your password.' });
    }
  };

  return (
    <AuthLayout title="Reset password" sub="Choose a new strong password for your account." link="Back to sign in" linkText="Already reset it?" linkTo="/login">
      <form className="auth-form" onSubmit={handleSubmit}>
        <FormField label="New password" error={errors.password}>
          <div className="password-field-shell" style={passwordFieldWrapperStyle}>
            <TextInput
              className="password-input-with-toggle"
              type={showPw ? 'text' : 'password'}
              placeholder="Use a strong password"
              value={form.password}
              onChange={set('password')}
              error={errors.password}
              style={passwordInputStyle}
            />
            <button type="button" className="auth-password-toggle" onClick={() => setShowPw(!showPw)} aria-label={showPw ? 'Hide password' : 'Show password'}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FormField>

        <FormField label="Confirm new password" error={errors.confirm}>
          <div className="password-field-shell" style={passwordFieldWrapperStyle}>
            <TextInput
              className="password-input-with-toggle"
              type={showConfirmPw ? 'text' : 'password'}
              placeholder="Repeat password"
              value={form.confirm}
              onChange={set('confirm')}
              error={errors.confirm}
              style={passwordInputStyle}
            />
            <button type="button" className="auth-password-toggle" onClick={() => setShowConfirmPw(!showConfirmPw)} aria-label={showConfirmPw ? 'Hide password' : 'Show password'}>
              {showConfirmPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FormField>

        <div className="auth-helper-copy" style={{ fontSize: '12px', color: 'var(--clr-text-3)', marginBottom: '1rem', lineHeight: 1.6 }}>
          Password must include at least 8 characters, an uppercase letter, a lowercase letter, a number, and a special character.
        </div>

        <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
          {loading ? loadingLabel : 'Reset password'}
        </Button>
      </form>
    </AuthLayout>
  );
}

